import { IMappedEventTarget } from "../Types";
import { PitchDetector } from "../analyzer/PitchDetector";
import { IPitchValue } from "../analyzer/abstraction/IPitchFilter";
import { IPoint } from "../helpers/Geometry";
import { getNoteFrequencyLog2 } from "../helpers/Notes";
import { AudioPlayer } from "../player/AudioPlayer";
import { MixedPlayer } from "../player/MultiPlayer";
import { PerfClock } from "../player/PerfClock";
import { Song } from "../player/Song";
import { SongPlayer } from "../player/SongPlayer";
import { synthCatalog } from "../player/SynthCatalog";
import { IClock } from "../player/abstraction/IClock";
import { IPlayer } from "../player/abstraction/IPlayer";
import { ISynth } from "../player/abstraction/ISynth";
import { ISingSession } from "../session/ISingSession";
import { SongView } from "../songView/SongView";
import { apiClient } from "./ApiClient";
import { ILoopRange, ISong } from "./Entities";

export type SyncMode = "pitch" | "song" | "none";

export interface IControlsState {
    position: number;
    loopRange: ILoopRange;
    isLoop: boolean;
    syncMode: SyncMode;
    showSong: boolean;
    showPitch: boolean;
    isSongMute: boolean;
    isMicMute: boolean;
    songSpeed: number;
    songKey: number;
    songSrc?: string;
    song?: ISong;
    micId?: string;
    synthId?: string;
    isSyncOn: boolean;
    scale?: IPoint
}

export type ControlsState = "play" | "stop" | "pause" | "record";

type ControlsEventMap = {
    "stateChanged": CustomEvent;
}

class Controls extends EventTarget implements IMappedEventTarget<ControlsEventMap> {

    protected _state: ControlsState;
    protected _synth: ISynth;
    protected _song: Song;
    protected _songView: SongView;
    protected _suspendPosUpdate: number;
    protected _position: number;
    protected _syncMode: SyncMode;
    protected _isLoop: boolean;
    protected _micInfo: MediaDeviceInfo;
    protected _songSrc: string;
    protected _updateCount: number;
    protected _lastEmptyTime: number;
    protected _isSyncOn: boolean;
    protected _isMicMute: boolean;
    protected _loopRange: ILoopRange;
    protected _player: IPlayer;
    protected _audioPlayer: AudioPlayer;
    protected _songPlayer: SongPlayer;

    constructor() {

        super();

        this._updateCount = 0;
        this._suspendPosUpdate = 0;
        this._position = 0;
        this._syncMode = "song";
        this._state = "stop";
        this._isLoop = false;
        this._isMicMute = true;
        this._audioPlayer = new AudioPlayer();
        this._songPlayer = new SongPlayer();
        this._player = new MixedPlayer(this._songPlayer, this._audioPlayer);

        this.clock = new PerfClock();

        this.detector = new PitchDetector();
        this.detector.clock = this.clock;



        this.detector.addEventListener("pitch", (ev : CustomEvent<IPitchValue>) => this.onPitch(ev.detail));

        this.clock.addEventListener("tick", ev => {

            this.setPositionAsync(ev.detail, "clock");
        });
    }

    getState() {
        return {
            isLoop: this.isLoop,
            isSongMute: this.isSongMute,
            loopRange: this.loopRange,
            position: this.position,
            showPitch: this.showPitch,
            showSong: this.showSong,
            songKey: this.songKey,
            syncMode: this.syncMode,
            songSpeed: this.songSpeed,
            songSrc: this._songSrc,
            micId: this._micInfo?.deviceId,
            synthId: this._synth?.deviceId,
            isSyncOn: this._isSyncOn,
            isMicMute: this.isMicMute,
            scale: {
                x: this._songView.pixelPerSeconds,
                y: this._songView.pixelPerHertz,
            }
        } as IControlsState;
    }

    async setStateAsync(state: IControlsState) {

        controls.beginUpdate();

        if (this._songView && state.scale) {
            this._songView.pixelPerHertz = state.scale.y;
            this._songView.pixelPerSeconds = state.scale.x;
        }

        if (state.song)
            await this.loadSongAsync(new Song(state.song));

        else if (state.songSrc)
            await this.loadSongByUrlAsync(state.songSrc);

        if (state.synthId)
            await this.setSynthIdAsync(state.synthId);

        if (state.micId)
            await this.setMicAsync(state.micId);

        this.isLoop = state.isLoop;
        this.isSongMute = state.isSongMute;
        this.showPitch = state.showPitch;
        this.showSong = state.showSong;
        this.songKey = state.songKey;
        this.syncMode = state.syncMode;
        this.songSpeed = state.songSpeed;
        this.isSyncOn = state.isSyncOn;

        if (state.loopRange)
            this.setLoopRange(state.loopRange.start, state.loopRange.duration);

        if (state.position)
            await this.setPositionAsync(state.position, "clock");


        await this.setIsMicMuteAsync(state.isMicMute);

        controls.endUpdate();
    }

    async setDefaultStateAsync() {
        const state = {
            isLoop: false,
            isMicMute: true,
            isSongMute: false,
            isSyncOn: true,
            showPitch: true,
            showSong: true,
            songKey: 0,
            songSpeed: 1,
            syncMode: "song",
            micId: (await this.getMicsAsync()).at(0)?.deviceId,
            synthId: (await synthCatalog.enumDevicesAsync()).at(0)?.deviceId
        } as IControlsState;

        await this.setStateAsync(state);
    } 

    beginUpdate() {
        this._updateCount++;
    }

    endUpdate() {
        this._updateCount--;
        if (this._updateCount == 0)
            this.onStateChanged();
    }

    async playAsync() {
        
        if (this._state == "play" || this._state == "record")
            return;

        const curPos = this._position;

        this.clock.start(20);

        this.clock.setTime(curPos);

        if (this._song && this._synth) {

            await this._player.setPositionAsync(curPos);

            this._player.playAsync();
        }

        this._state = "play";

        this.onStateChanged();
    }

    async startRecordAsync() {

        if (this._state == "record")
            return;

        this._lastEmptyTime = undefined;

        await this.playAsync();

        await this.setIsMicMuteAsync(false);

        this._state = "record";

        this.onStateChanged();
    }

    stopRecord(){

        this.detector.stop();

        if (this._state == "record")
            this._state = "play";

        this.onStateChanged();
    }

    protected onPitch(value: IPitchValue) {

        if (value.pitch) {

            if (this._isSyncOn && this._syncMode == "pitch") {

                this._songView.origin.y = Math.log2(value.pitch) - this._songView.viewRect.height / 2;
                this._songView.updateTransform("position");
            }

            if (this._lastEmptyTime) {
                this._songView.addPitch(this._lastEmptyTime, undefined, 0);
                this._lastEmptyTime = undefined;
            }

            this._songView.addPitch(value.time, value.pitch, value.power);
        }
        else {
            this._lastEmptyTime = value.time;
            this._songView.requestRenderAsync();
        }
    }

    async stopAsync() {

        await this._player.stopAsync();
        this.stopRecord();

        this.clock.stop();

        await this.setPositionAsync(0.0001, "view");
        await this.setPositionAsync(0, "clock");

        this._state = "stop";

        this.onStateChanged();
    }

    async pauseAsync() {

        this.detector.pause();
        await this._player.pauseAsync();
        this.clock.stop();

        this._state = "pause";

        this.onStateChanged();
    }

    attachView(songView: SongView) {

        this._songView = songView;

        this._songView.setDetector(this.detector);

        this._songView.addEventListener("positionChanged", () => {
            this.setPositionAsync(this._songView.position, "view");
        });

        songView.addEventListener("viewChanged", (ev: CustomEvent) => {
            if (ev.detail == "scale")
                this.onStateChanged();
        });
    }

    async loadSongAsync(song: Song, src?: string) {

        this._song = song;
        this._songSrc = src;

        this.songView.setSong(song);

        this._songPlayer.load(this._song, this._synth, this.clock);

        if (song.preSynthTrack)
            await this._audioPlayer.loadAsync(song.preSynthTrack);
        else
            this._audioPlayer.close();

        if (this._song)
            this._song.loopRange = this._loopRange;

        this.onStateChanged();
    }

    async loadSongByUrlAsync(url: string, channel?: number) {

        const song = await apiClient.parseMidiAsync(url, channel);

        await this.loadSongAsync(new Song(song), url);
    }

    async loadSessionAsync(session: ISingSession) {

    }

    async newSesionAsync() {

    }

    get isSyncOn() {
        return this._isSyncOn;
    }

    set isSyncOn(value: boolean) {
        this._isSyncOn = value;
        this.onStateChanged();
    }

    get syncMode() {
        return this._syncMode;
    }

    set syncMode(value: SyncMode) {
        this._syncMode = value;
        this.onStateChanged();
    }

    setLoopRange(start: number, duration: number) {

        this._loopRange = duration <= 0 ? undefined : {
            start,
            duration
        };

        if (this._song)
            this._song.loopRange = this._loopRange;

        this.onStateChanged();
    }

    get loopRange() {
        return this._loopRange;
    }

    markStartLoop() {
        const duration = (this._loopRange?.duration + this._loopRange?.start - this._position);
        this.setLoopRange(this._position, !duration || isNaN(duration) ? 1 : duration );
    }

    markEndLoop() {
        this.setLoopRange(this._loopRange?.start, this._position - (this._loopRange?.start ?? 0));
    }

    set isLoop(value: boolean) {
        this._isLoop = value;
        this.onStateChanged();
    }

    get isLoop() {
        return this._isLoop;
    }

    async setIsMicMuteAsync(value: boolean) {

        if (!value && this._micInfo?.deviceId) {
            this.detector.startAsync(this._micInfo.deviceId);
            this._isMicMute = false;
        }
        else {
            this.detector.stop();
            this._isMicMute = true;
        }

        this.onStateChanged();
    }

    get isMicMute() {
        return this._isMicMute;
    }

    set isSongMute(value: boolean) {
        this._songPlayer.volume = value ? 0 : 1;
        this.onStateChanged();
    }

    get isSongMute() {
        return this._songPlayer.volume == 0;
    }

    set songSpeed(value: number) {

        if (this.song)
            this.song.speed = value;

        if (this._position)
            this.setPositionAsync(this._position / value, "view");

        this.songView.setSong(this.song);
    }

    get songSpeed() {
        return this.song?.speed ?? 1;
    }

    set songKey(value: number) {

        if (this.song)
            this.song.key = value;

        this.songView.requestRenderAsync();
    }

    get songKey() {
        return this.song?.key ?? 0;
    }

    get showPitch() {
        return this._songView.pitchTrack.visible;
    }

    set showPitch(value: boolean) {
        this._songView.pitchTrack.visible = value;
        this.onStateChanged();
    }

    get showSong() {
        return this._songView.songTrack.visible;
    }

    set showSong(value: boolean) {
        this._songView.songTrack.visible = value;
        this.onStateChanged();
    }

    get mic() {
        return this._micInfo;
    }


    async setMicAsync(device: MediaDeviceInfo | string) {

        if (typeof device == "string")
            device = (await controls.getMicsAsync()).find(a => a.deviceId == device);

        if (this._micInfo?.deviceId == device?.deviceId)
            return;

        this._micInfo = device;
        if (!this.isMicMute) {

            this.detector.stop();

            if (this._micInfo)
                this.detector.startAsync(this._micInfo.deviceId);
        }

        this.onStateChanged();
    }

    async getMicsAsync() {

        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter(a => a.kind == "audioinput");
    }

    get position() {
        return this._position;
    }

    async setPositionAsync(value: number, source: "view" | "clock") {

        if (this._position == value)
            return;

        if (this._suspendPosUpdate)
            return;

        this._suspendPosUpdate++;

        this._position = value;

        if (source == "clock") {

            this._songView.position = value;

            if (this._isSyncOn) {

                if (this._syncMode == "song" && this._songPlayer.lastNote) {

                    const newOrigin = { ...this._songView.origin }

                    newOrigin.x = Math.max(this._songView.getMaxViewRect().x, this._songPlayer.lastNote.time - this._songView.viewRect.width * 0.3);

                    newOrigin.y = getNoteFrequencyLog2(this._songPlayer.lastNote.note) - this._songView.viewRect.height / 2;

                    this._songView.animateOrigin(newOrigin, 300);
                }
            }

        }
        else {

            this.clock.setTime(value); 

            await this._player.setPositionAsync(value);

            this.onStateChanged();
        }

        this._suspendPosUpdate--;


        if (source == "clock" && this._isLoop && this.loopRange?.start && this.loopRange?.duration) {
            if (value > this.loopRange.start + this.loopRange.duration)
                await this.setPositionAsync(this.loopRange.start, "view");
        }


    }

    get state() {
        return this._state;
    }

    get synth() {
        return this._synth;
    }

    async setSynthIdAsync(value: string) {

        if (!value)
            await this.setSynthAsync(undefined);
        else {
            const device = await synthCatalog.getDeviceByIdAsync(value);
            if (device)
                await this.setSynthAsync(device.create());
        }

    }

    async setSynthAsync(value: ISynth) {

        this._synth = value;

        if (this._songPlayer.state == "play") {
            await this._songPlayer.pauseAsync();
            this._songPlayer.synth = value;
            await this._songPlayer.playAsync();
        }
        else
            this._songPlayer.synth = value;

        this.onStateChanged();
    }

    get song() {
        return this._song;
    }

    get songView() {
        return this._songView;
    }

    protected onStateChanged() {
        if (this._updateCount == 0)
            this.dispatchEvent(new CustomEvent("stateChanged"));
    }

    session: ISingSession;

    readonly detector: PitchDetector;

    readonly clock: IClock;
}

export const controls = new Controls();